/**
 * Is a link fit to be a product's buy link? Shared by the lookup
 * (find-product), the paid search fallback and the page read path, which
 * overlays the cached link the box writes into `products.external_link`.
 *
 * Two ways a link fails, both seen live on the UK demo page on 2026-09-18:
 *  - its page is about another model of the same line (Braun's XT5100 page
 *    for the XT5200, BaByliss's 7897U page for the 7899U);
 *  - it is not a shop: a manual, support, news or community page
 *    (manuals.co.uk, argos-support.co.uk, channel.panasonic.com).
 *
 * Pure functions, no I/O.
 */

import { isDtcStoreUrl } from "../../data/purchase-channels";
import type { Product } from "../../types/product.type";

const STOPWORDS = new Set([
  "the", "and", "for", "with", "de", "la", "le", "les", "des", "du", "et", "pour", "avec",
]);

/** Lower-case words of two characters or more, accents removed. */
export function productTokens(text: string): string[] {
  return text
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .split(/[^a-z0-9]+/)
    .filter((token) => token.length >= 2 && !STOPWORDS.has(token));
}

/**
 * A number with a unit is a spec, not a model: 128gb and 256gb are variants of
 * one phone, 65w and 90w of one charger. A decimal written with a separator
 * splits into two tokens ("5.0Ah" and the slug "5-0ah" both give "5", "0ah"),
 * so a unit missing from this list turns the tail into a false sibling code
 * ("0ah" against a product's "5ah").
 */
const SPEC_TOKEN =
  /^\d+(?:\.\d+)?(?:gb|tb|mb|mah|ah|wh|w|kw|v|hz|khz|ghz|ml|cl|l|oz|lb|lbs|kg|g|mm|cm|m|in|inch|ft|yd|k|p|x|pk|pack|pcs|pc|ct|mp|mph|rpm|db|qt|gal|hp|btu|psi|bar|lm|cc|amp|amps|hr|hrs|min|nm|cfm|gpm|fps|dpi|awg|mil)$/;

/** A model code mixes letters and digits ("xt5200", "7899u", "gb80"). */
export function isModelCode(token: string): boolean {
  return /\d/.test(token) && /[a-z]/.test(token) && !SPEC_TOKEN.test(token);
}

/** A model code with its digits blanked: "xt5200" and "xt5100" share one. */
export function modelShape(token: string): string {
  return token.replace(/\d/g, "0");
}

/**
 * Does this text name another model of the product's line: a model code
 * shaped like one of the product's (XT5100 against XT5200, 7897U against
 * 7899U) while the product's own code of that shape is absent? Text naming
 * no model, or the product's own among others, is fine. Digit-only numbers
 * are ignored: sizes, years and shop ids look alike.
 */
export function namesOtherModel(
  text: string | undefined,
  product: Pick<Product, "title">,
): boolean {
  if (!text || !product.title) return false;
  const own = productTokens(product.title).filter(isModelCode);
  if (own.length === 0) return false;
  const found = new Set(productTokens(text).filter(isModelCode));
  return [...found].some((token) => {
    if (own.includes(token)) return false;
    const sameShape = own.filter((code) => modelShape(code) === modelShape(token));
    return sameShape.length > 0 && !sameShape.some((code) => found.has(code));
  });
}

/**
 * Does a link's page name another model? Only the path is read: a host like
 * `i5.walmartimages.com` carries no model.
 */
export function linkNamesOtherModel(
  href: string | undefined,
  product: Pick<Product, "title">,
): boolean {
  if (!href) return false;
  try {
    return namesOtherModel(decodeURIComponent(new URL(href).pathname), product);
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------ *
 * Hosts
 * ------------------------------------------------------------------ */

/**
 * Labels a country registry sells names under: `co.uk`, `com.au`, `co.nz`,
 * `com.br`, `com.mx`, `co.jp`, `ne.jp`, `com.co`, `co.in`, `com.ar`,
 * `com.sg`, `gob.mx`. Under a two-letter country domain such a label is part
 * of the public suffix, not of the shop's own name.
 */
const REGISTRY_SECOND_LEVEL_LABELS = new Set(["co", "com", "net", "org", "ac", "gov", "edu", "nom", "ne", "or", "gob"]);

/**
 * A host in three parts: the labels in front of the site's own name, that
 * name, and the public suffix. pro.resound.com → ["pro"], "resound", ["com"];
 * shop.argos.co.uk → ["shop"], "argos", ["co", "uk"]; falabella.com.co →
 * [], "falabella", ["com", "co"]. One rule for every two-part country
 * suffix: a registry label under a two-letter country domain.
 */
export function hostParts(host: string): { subdomains: string[]; name: string; suffix: string[] } {
  const labels = host.toLowerCase().replace(/\.$/, "").replace(/^www\./, "").split(".").filter(Boolean);
  const last = labels[labels.length - 1] ?? "";
  const second = labels[labels.length - 2] ?? "";
  const suffixLength = labels.length >= 3 && last.length === 2 && REGISTRY_SECOND_LEVEL_LABELS.has(second) ? 2 : 1;
  const nameAt = labels.length - suffixLength - 1;
  return {
    subdomains: labels.slice(0, Math.max(0, nameAt)),
    name: labels[nameAt] ?? "",
    suffix: labels.slice(-suffixLength),
  };
}

/* ------------------------------------------------------------------ *
 * Pages that are not a shop's product page
 * ------------------------------------------------------------------ */

/**
 * Words that mark a page as help, documentation or editorial rather than a
 * shop, as a host word (argos-support.co.uk) or a path segment.
 */
const NON_SHOP_WORDS = new Set([
  "support", "manual", "manuals", "help", "faq", "faqs", "docs", "forum", "forums", "community",
  "blog", "blogs", "news", "newsroom", "press", "channel", "wiki", "review", "reviews",
  "investor", "investors", "careers",
]);

/**
 * Documents and labels (2026-09-25: a Starkey brochure under PdfAssets, a
 * Phonak product-information sheet under /documents/, a Pfizer drug label on
 * labeling.pfizer.com). A whole host label or path segment only: a shop
 * named packaging-labelling.com sells labelling machines.
 */
const DOCUMENT_WORDS = new Set([
  "documents", "document", "brochure", "brochures", "pdfassets", "datasheet", "datasheets",
  "labeling", "labelling",
]);

/** A document, not a page: .pdf, .doc, .docx. */
const DOCUMENT_PATH = /\.(pdf|docx?)$/;

/** "Manual" in the languages of our markets. */
const MANUAL_WORDS = new Set(["manual", "manuals", "manuel", "manuels", "manuale", "manuali"]);

/** A plural can only be documents: "manuals-and-guides", "manuali_utilizzo". */
const MANUAL_PLURALS = new Set(["manuals", "manuels", "manuali"]);

/**
 * The kind of document, which English puts before "manual" (a user manual,
 * an owner's manual, an instruction manual, an operation manual) and French,
 * Italian and Spanish after it (manuel d'utilisation, manuale d'uso, manual
 * de usuario). Anything else makes "manual" a kind of product: manual
 * wheelchairs, a manual coffee grinder, manual toothbrushes, a manual
 * operation valve, or a book titled "…Service Dog Training Manual".
 */
const MANUAL_KIND_BEFORE = new Set([
  "user", "users", "owner", "owners", "instruction", "instructions", "operating", "operation",
  "operator", "operators", "installation", "service", "reference",
]);
const MANUAL_KIND_AFTER = new Set([
  "utilisation", "utilisateur", "utilizzo", "uso", "usuario", "usuarios", "instrucciones", "istruzioni",
  "instrucoes",
]);
const MANUAL_LINK_WORDS = new Set(["s", "de", "d", "del", "di", "do", "da"]);

/**
 * Is this path segment a manual? The segment itself ("/manuals/",
 * "/manual/17299856"), a plural anywhere in it ("manuals-and-guides",
 * "manuali_utilizzo"), or a singular with its kind of document next to it
 * ("user-manual", "owner-s-manual", "SPEED_X_USER_MANUAL.html",
 * "manuel-d-utilisation").
 */
function isManualSegment(segment: string): boolean {
  if (MANUAL_WORDS.has(segment)) return true;
  const words = segment.split(/[^a-z0-9]+/).filter(Boolean);
  return words.some((word, i) => {
    if (MANUAL_PLURALS.has(word)) return true;
    if (!MANUAL_WORDS.has(word)) return false;
    const before = words.slice(0, i).filter((w) => !MANUAL_LINK_WORDS.has(w)).pop();
    const after = words.slice(i + 1).find((w) => !MANUAL_LINK_WORDS.has(w));
    return (before !== undefined && MANUAL_KIND_BEFORE.has(before)) || (after !== undefined && MANUAL_KIND_AFTER.has(after));
  });
}

/**
 * Path segments of a page that lists a range rather than sells one product:
 * Lincat's `/brand/opus-800/` stood as the buy link for an Opus oven. Only
 * when at most one segment follows (the range's own name): shops also file
 * product pages under them (richelieu.com/ca/en/category/…/1059384/sku-…,
 * houseoffraser.co.uk/brand/nike/swoosh-headband-770033).
 */
const ARCHIVE_SEGMENTS = new Set([
  "brand", "brands", "archive", "archives", "category", "categories", "search",
]);

/**
 * Subdomains of a site for professionals or partners, not shoppers:
 * pro.resound.com, partnerhub.unox.com, trade.dusk.com, dealer.tojagrid.ca.
 * A subdomain only: basspro.com, herbspro.com and trade names like them are
 * shops.
 */
const PORTAL_SUBDOMAINS = new Set([
  "pro", "professional", "professionals", "partner", "partners", "partnerhub", "dealer", "dealers",
  "b2b", "trade", "hcp",
  // Customer-service sites (2026-09-28 dry run: service.anker.com's support
  // article stood as an Anker dock's link).
  "service", "customerservice", "customercare", "kb", "knowledgebase", "helpcenter", "helpdesk",
]);

/** Professional portals on a domain of their own (clinicians, practitioners). */
const KNOWN_PORTAL_DOMAINS = [
  "widexpro.com", "phonakpro.com", "starkeypro.com", "signia-pro.com", "pureencapsulationspro.com",
];

/**
 * Language and country codes a site files its pages under ("/en-ca/",
 * "/fr/", "/ca/en/", "/GB_en/"). A path made of nothing else is the site's
 * home page for that market (2026-09-28: "Our pick" opened
 * https://www.starkeycanada.ca/, Starkey's home page, for a Starkey Picasso).
 */
const LOCALE_SEGMENT_CODES = new Set([
  "en", "fr", "de", "es", "it", "pt", "nl", "pl", "sv", "ja", "zh", "da", "fi", "no", "ko", "ru", "cy", "ga",
  "ca", "us", "uk", "gb", "au", "nz", "ie", "ch", "at", "be", "mx", "br", "ar", "cl", "co", "jp", "se", "in",
  "eu", "int",
]);
const LOCALE_SEGMENT_WORDS = new Set(["global", "intl", "international", "worldwide", "home"]);
/** A home page's own file name: /index.html, /default.aspx, /accueil. */
const HOME_FILE = /^(index|default|homepage|accueil|inicio|startseite|start)(\.(html?|php|aspx?|jsp))?$/;

export function isLocaleSegment(segment: string): boolean {
  if (LOCALE_SEGMENT_WORDS.has(segment)) return true;
  const pair = /^([a-z]{2,3})(?:[-_]([a-z]{2,3}))?$/.exec(segment);
  return !!pair && LOCALE_SEGMENT_CODES.has(pair[1]) && (!pair[2] || LOCALE_SEGMENT_CODES.has(pair[2]));
}

/**
 * Query keys that do not change which page is shown: campaign and click ids,
 * and the market or language a home page is served in. Anything else
 * (`?product=12`, `?route=product/product&product_id=42`) can name a product
 * on the root path, so such a URL is not called a home page.
 */
function isNeutralQueryKey(key: string): boolean {
  return /^(utm_\w+|ref|referrer|srsltid|gclid|gbraid|wbraid|msclkid|fbclid|lang|language|locale|country|region|currency|hl|gl)$/.test(
    key,
  );
}

/**
 * A store's landing page, named: /shop, /store, /buy, /boutique, /tienda
 * (2026-09-28 review: https://www.apple.com/ca/shop/,
 * https://www.stryd.com/uk/en/store, https://www.neuhauschocolates.com/en_FR/shop/
 * got "Our pick"). It sells nothing in particular: a home page by another name.
 */
const STORE_LANDING_SEGMENTS = new Set(["shop", "store", "buy", "boutique", "tienda"]);

/**
 * A site's home page: nothing but locale segments in the path (or those and
 * one store landing segment), and no query naming a page.
 */
function isHomePage(parsed: URL, segments: readonly string[]): boolean {
  const named = segments.filter((segment) => !isLocaleSegment(segment) && !HOME_FILE.test(segment));
  if (named.length > 1 || (named.length === 1 && !STORE_LANDING_SEGMENTS.has(named[0]))) return false;
  for (const key of parsed.searchParams.keys()) {
    if (!isNeutralQueryKey(key.toLowerCase())) return false;
  }
  return true;
}

/**
 * Help and editorial sections named with more than one word
 * (jabra.com/supportpages/…, 2026-09-28; support-pages, product-support,
 * helpcenter, help-and-support). Read in the FIRST path segment after the
 * locale only, and only when every word of it comes from these lists: a
 * product slug that merely contains a support word ("lumbar-support-pillow",
 * "/products/knee-support") is a shop page.
 */
const COMPOUND_HEADS = new Set(["support", "help", "faq", "faqs", "docs", "manual", "manuals", "forum", "forums", "community", "wiki"]);
const COMPOUND_TAILS = new Set(["pages", "page", "center", "centre", "desk", "hub", "portal", "site", "zone", "area", "corner", "section"]);
const COMPOUND_OWNERS = new Set(["product", "products", "customer", "customers", "tech", "technical", "online", "service", "services"]);
const COMPOUND_JOINERS = new Set(["and", "et", "y", "und", "e", "n"]);

function compoundNonShopWord(segment: string): string | null {
  const words = segment.split(/[-_.+]+/).filter((word) => word && !COMPOUND_JOINERS.has(word));
  const known = (word: string) => COMPOUND_HEADS.has(word) || COMPOUND_TAILS.has(word) || COMPOUND_OWNERS.has(word);
  if (words.length >= 2 && words.every(known)) return words.find((word) => COMPOUND_HEADS.has(word)) ?? null;
  // Written as one word: "supportpages", "helpcenter", "productsupport".
  if (words.length === 1) {
    const [word] = words;
    for (const head of COMPOUND_HEADS) {
      if (word.startsWith(head) && known(word.slice(head.length))) return head;
      if (word.endsWith(head) && COMPOUND_OWNERS.has(word.slice(0, word.length - head.length))) return head;
    }
  }
  return null;
}

/** The word that marks this URL as not a shop page, or null for a shop page. */
export function nonShopWord(url: string): string | null {
  try {
    const parsed = new URL(url);
    const host = parsed.hostname.toLowerCase().replace(/^www\./, "").replace(/\.$/, "");
    const labels = host.split(".");
    const hostWord =
      labels
        .flatMap((label) => label.split("-"))
        // manualslib.com, manualzz.com, manuals.plus, manymanuals.com: sites of manuals.
        .find((word) => NON_SHOP_WORDS.has(word) || word.startsWith("manual") || word.includes("manuals")) ??
      labels.find((label) => DOCUMENT_WORDS.has(label));
    if (hostWord) return hostWord;
    const portal =
      hostParts(host).subdomains.find((label) => PORTAL_SUBDOMAINS.has(label)) ??
      KNOWN_PORTAL_DOMAINS.find((domain) => host === domain || host.endsWith(`.${domain}`));
    if (portal) return portal;
    const path = parsed.pathname.toLowerCase();
    if (DOCUMENT_PATH.test(path)) return "document";
    const segments = path.split("/").filter(Boolean);
    if (isHomePage(parsed, segments)) return "home";
    const first = segments.find((segment) => !isLocaleSegment(segment));
    const compound = first ? compoundNonShopWord(first) : null;
    if (compound) return compound;
    return (
      segments.find(
        (segment, i) =>
          NON_SHOP_WORDS.has(segment) ||
          DOCUMENT_WORDS.has(segment) ||
          isManualSegment(segment) ||
          (ARCHIVE_SEGMENTS.has(segment) && segments.length - i - 1 <= 1),
      ) ?? null
    );
  } catch {
    return "invalid url";
  }
}

export function isNonShopPage(url: string): boolean {
  return nonShopWord(url) !== null;
}

/**
 * A shop page that does not name another model of the product. A brand sold
 * only on its own site links to that store as the registry names it, its
 * home page included (frame.work, shop.fairphone.com): that link is the
 * product's buy link, not a page to re-resolve.
 */
export function isUsableBuyLink(
  href: string | undefined,
  product: Pick<Product, "title">,
): boolean {
  if (!href) return false;
  if (isDtcStoreUrl(href)) return true;
  return !isNonShopPage(href) && !linkNamesOtherModel(href, product);
}

/**
 * The buy link the offers engine should read first: a usable shop link that
 * is not Amazon (Amazon links travel as knownAmazonOffer). Undefined otherwise.
 */
export function knownShopOfferUrl(
  href: string | null | undefined,
  product: Pick<Product, "title">,
): string | undefined {
  if (!href || href.length > 2048) return undefined;
  let host: string;
  try {
    const url = new URL(href);
    if (url.protocol !== "https:" && url.protocol !== "http:") return undefined;
    host = url.hostname.toLowerCase();
  } catch {
    return undefined;
  }
  if (/(^|\.)amazon\.|(^|\.)amzn\.|^a\.co$|(^|\.)audible\./.test(host)) return undefined;
  return isUsableBuyLink(href, product) ? href : undefined;
}
